import { SharedLoanDisbursement, SharedLoanRepayment, V1ResponseMetadata } from '@bukalapak/openapi-schema/vptrader/loan/v1/public/loan-domain';
import VPNonTraderAPIClient from '~/api/vpnontrader';

type InitialState = {
  disbursement: SharedLoanDisbursement;
  repayments: SharedLoanRepayment[];
  repaymentsMeta: V1ResponseMetadata;
  isLoadingDisbursement: boolean;
  isErrorLoadingDisbursement: boolean;
  isErrorLoadingRepayments: boolean;
  isLoadingRepayments: boolean;
};

const initialState: InitialState = {
  disbursement: {},
  repayments: [],
  repaymentsMeta: {
    offset_pagination: {
      offset: 0,
      limit: 10,
      total: 0,
    },
  },
  isLoadingDisbursement: true,
  isErrorLoadingDisbursement: false,
  isErrorLoadingRepayments: false,
  isLoadingRepayments: true,
};

const getters = {
  getDisbursement(state) {
    return state.disbursement;
  },
  getRepayments(state) {
    return state.repayments;
  },
  getRepaymentsMeta(state) {
    return state.repaymentsMeta;
  },
  isLoadingDisbursement(state) {
    return state.isLoadingDisbursement;
  },
  isErrorLoadingDisbursement(state) {
    return state.isErrorLoadingDisbursement;
  },
  isLoadingRepayments(state) {
    return state.isLoadingRepayments;
  },
  isErrorLoadingRepayments(state) {
    return state.isErrorLoadingRepayments;
  },
  isRepaymentsEmpty(state) {
    return state.repayments.length === 0 && !state.isLoadingRepayments && !state.isErrorLoadingRepayments;
  },
  isRepaymentsCompleted(state) {
    return state.repayments.length === state.repaymentsMeta.offset_pagination.total;
  },
};

const actions = {
  async fetchDisbursement({ commit, dispatch }, id: string) {
    commit('setLoadingDisbursement', true);
    commit('setErrorLoadingDisbursement', false);
    try {
      const response = await VPNonTraderAPIClient.getDisbursement(id);
      commit('setDisbursement', response.disbursement);
    } catch (error) {
      commit('setErrorLoadingDisbursement', true);
      dispatch('catchError', { error });
    } finally {
      commit('setLoadingDisbursement', false);
    }
  },
  async fetchRepayments({ commit, dispatch }, payload: { disbursementId: string; limit: number; offset: number }) {
    commit('setLoadingRepayments', true);
    commit('setErrorLoadingRepayments', false);
    try {
      const response = await VPNonTraderAPIClient.listRepayments(payload);
      commit('appendRepayments', response.repayments);
      commit('setRepaymentsMeta', response.meta);
    } catch (error) {
      commit('setErrorLoadingRepayments', true);
      dispatch('catchError', { error });
    } finally {
      commit('setLoadingRepayments', false);
    }
  },
};

const mutations = {
  setDisbursement(state, disbursement) {
    state.disbursement = disbursement;
  },
  setLoadingDisbursement(state, isLoadingDisbursement) {
    state.isLoadingDisbursement = isLoadingDisbursement;
  },
  setErrorLoadingDisbursement(state, isErrorLoadingDisbursement) {
    state.isErrorLoadingDisbursement = isErrorLoadingDisbursement;
  },
  appendRepayments(state, repayments) {
    state.repayments = [...state.repayments, ...repayments];
  },
  setRepaymentsMeta(state, repaymentsMeta) {
    state.repaymentsMeta = repaymentsMeta;
  },
  setLoadingRepayments(state, isLoadingRepayments) {
    state.isLoadingRepayments = isLoadingRepayments;
  },
  setErrorLoadingRepayments(state, isErrorLoadingRepayments) {
    state.isErrorLoadingRepayments = isErrorLoadingRepayments;
  },
  resetState(state) {
    state.disbursement = initialState.disbursement;
    state.repayments = initialState.repayments;
    state.repaymentsMeta = initialState.repaymentsMeta;
    state.isLoadingDisbursement = initialState.isLoadingDisbursement;
    state.isErrorLoadingDisbursement = initialState.isErrorLoadingDisbursement;
    state.isLoadingRepayments = initialState.isLoadingRepayments;
    state.isErrorLoadingRepayments = initialState.isErrorLoadingRepayments;
  }
};

export default {
  state: () => ({ ...initialState }),
  getters,
  actions,
  mutations,
};
